import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  ConsentPreferences,
  ConsentCategory,
  getConsentRecord,
  saveConsentRecord,
  hasConsent,
  getConsentPreferences,
  acceptAllConsent,
  rejectNonEssentialConsent,
  clearConsentRecord,
  shouldShowBanner,
  detectGPC,
  applyGPCOptOut,
  clearNonEssentialStorage,
  DEFAULT_PREFERENCES,
} from '../lib/consent';

interface ConsentContextValue {
  preferences: ConsentPreferences;
  showBanner: boolean;
  showSettings: boolean;
  isGPCEnabled: boolean;
  hasConsent: (category: ConsentCategory) => Promise<boolean>;
  acceptAll: () => Promise<void>;
  rejectNonEssential: () => Promise<void>;
  updatePreferences: (preferences: ConsentPreferences) => Promise<void>;
  openSettings: () => Promise<void>;
  closeSettings: () => void;
  closeBanner: () => void;
  withdrawConsent: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

interface ConsentProviderProps {
  children: ReactNode;
}

export function ConsentProvider({ children }: ConsentProviderProps) {
  const [preferences, setPreferences] = useState<ConsentPreferences>(DEFAULT_PREFERENCES);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isGPCEnabled, setIsGPCEnabled] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initConsent = async () => {
      const gpcEnabled = detectGPC();
      setIsGPCEnabled(gpcEnabled);

      if (gpcEnabled) {
        const record = await getConsentRecord();
        if (!record || !record.gpcEnabled) {
          await applyGPCOptOut();
          const prefs = await getConsentPreferences();
          setPreferences(prefs);
          setShowBanner(false);
          setIsInitialized(true);
          return;
        }
      }

      const currentPreferences = await getConsentPreferences();
      setPreferences(currentPreferences);
      const bannerStatus = await shouldShowBanner();
      setShowBanner(bannerStatus);
      setIsInitialized(true);
    };

    initConsent();
  }, []);

  const checkConsent = useCallback(
    async (category: ConsentCategory): Promise<boolean> => {
      return await hasConsent(category);
    },
    []
  );

  const acceptAll = useCallback(async () => {
    console.log('[ConsentContext.acceptAll] Called');
    await acceptAllConsent();
    console.log('[ConsentContext.acceptAll] acceptAllConsent complete, reading preferences');
    const newPreferences = await getConsentPreferences();
    console.log('[ConsentContext.acceptAll] Got preferences:', newPreferences);
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowSettings(false);
    console.log('[ConsentContext.acceptAll] Complete');
  }, []);

  const rejectNonEssential = useCallback(async () => {
    await rejectNonEssentialConsent();
    clearNonEssentialStorage();
    const newPreferences = await getConsentPreferences();
    setPreferences(newPreferences);
    setShowBanner(false);
    setShowSettings(false);
  }, []);

  const updatePreferences = useCallback(async (newPreferences: ConsentPreferences) => {
    console.log('[ConsentContext.updatePreferences] Called with:', newPreferences);
    await saveConsentRecord(newPreferences);
    console.log('[ConsentContext.updatePreferences] saveConsentRecord complete');

    const verification = await getConsentPreferences();
    console.log('[ConsentContext.updatePreferences] Verification read:', verification);

    setPreferences(newPreferences);
    setShowSettings(false);
    setShowBanner(false);
    console.log('[ConsentContext.updatePreferences] State updated, modal and banner closed');

    if (!newPreferences.analytics || !newPreferences.functionality || !newPreferences.advertising) {
      clearNonEssentialStorage();
    }
  }, []);

  const openSettings = useCallback(async () => {
    console.log('[ConsentContext.openSettings] Called');
    const currentPreferences = await getConsentPreferences();
    console.log('[ConsentContext.openSettings] Got preferences from cookie:', currentPreferences);

    setPreferences(currentPreferences);

    setTimeout(() => {
      console.log('[ConsentContext.openSettings] Opening modal');
      setShowSettings(true);
    }, 0);
  }, []);

  const closeSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  const closeBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  const withdrawConsent = useCallback(() => {
    clearConsentRecord();
    clearNonEssentialStorage();
    setPreferences(DEFAULT_PREFERENCES);
    setShowBanner(true);
  }, []);

  const value: ConsentContextValue = {
    preferences,
    showBanner,
    showSettings,
    isGPCEnabled,
    hasConsent: checkConsent,
    acceptAll,
    rejectNonEssential,
    updatePreferences,
    openSettings,
    closeSettings,
    closeBanner,
    withdrawConsent,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentContextValue {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}
